import { faCalendarDays, faCircleXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import format from 'date-fns/format'
import useTranslation from 'next-translate/useTranslation'
import { CSSProperties, ReactNode, useCallback, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import Button from '@unreserved-frontend-v2/ui/button/button'
import DatePicker, { DatePickerProps } from '@unreserved-frontend-v2/ui/date-picker/date-picker'
import { FlexCol } from '@unreserved-frontend-v2/ui/flex/flex-col'
import { ModalLite } from '@unreserved-frontend-v2/ui/modal-lite/modal-lite'
import { massageDate } from '@unreserved-frontend-v2/utils/date-formatters'

export type DatePickerModalProps = {
  /** A unique identifier for the input */
  id?: string

  /** Whether or not there is an error with this input’s values */
  isErrored?: boolean

  /** A component used to display the label/title of the input */
  label?: ReactNode

  /** whether the date is required */
  isRequired?: boolean

  /** classes to place on the date picker */
  className?: string

  /** Container specific override styles */
  containerClassName?: string

  /** Container specific inline styles */
  containerInlineStyle?: CSSProperties

  /** Handler called when a value is selected. */
  onValueChange: (value: string | null) => void

  /** initial value of date picker if provided */
  defaultValue?: string

  /** Overrides for the modal background */
  modalBackgroundClassName?: string
} & DatePickerProps

/**
 * Convenience component that wraps the date picker with a modal implementation
 */
export const DatePickerModal = ({
  id,
  isErrored,
  isRequired,
  label,
  containerClassName,
  containerInlineStyle,
  onValueChange,
  defaultValue,
  modalBackgroundClassName,
  /* eslint-disable-next-line unused-imports/no-unused-vars --
     Need className here so that it's not part of "rest" which is spread as props below. */
  className,
  ...rest
}: DatePickerModalProps) => {
  const { t } = useTranslation('data-form-instance')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dateValue, setDateValue] = useState<Date | string | null | undefined>(
    defaultValue ? massageDate(defaultValue) : defaultValue
  )

  const displayDate = useCallback((date: Date) => {
    return format(date, 'MMM d, yyyy')
  }, [])

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const clear = useCallback(() => {
    setDateValue(null)
    onValueChange(null)
  }, [onValueChange])

  return (
    <FlexCol id={id} className={containerClassName} style={containerInlineStyle}>
      {label}

      <Button
        className={twMerge(
          'h-10 w-full rounded border border-shades-200 bg-white pl-4 text-left',
          className,
          isErrored ? 'border-red-600 bg-red-50 text-red-600 outline-red-600' : null
        )}
        variant="trueUnstyled"
        onClick={openModal}
        id={id}
      >
        {dateValue ? (
          displayDate(dateValue as Date)
        ) : (
          <span className={twMerge('text-shades', isErrored ? 'text-red-400' : null)}>
            {isRequired ? t('ui:schema-field.required') : t('ui:schema-field.optional')}
          </span>
        )}

        {dateValue ? (
          <FontAwesomeIcon
            onClick={(e) => {
              e.stopPropagation()
              clear()
            }}
            className="relative float-right pr-3.5 pt-[3px] text-shades-200"
            icon={faCircleXmark}
          />
        ) : (
          <FontAwesomeIcon className="relative float-right pr-4 pt-0.5" icon={faCalendarDays} />
        )}
      </Button>

      {isModalOpen && (
        <ModalLite
          headerClassName="hidden"
          windowClassName="w-auto"
          footerClassName="hidden"
          backgroundClassName={modalBackgroundClassName}
          onClose={closeModal}
        >
          <DatePicker
            defaultDates={dateValue ? [dateValue as Date] : []}
            onChange={(dates) => {
              setDateValue(dates[0])
              onValueChange(format(dates[0], 'yyyy-MM-dd'))
              closeModal()
            }}
            {...rest}
          />
        </ModalLite>
      )}
    </FlexCol>
  )
}
